import { useCallback, useRef, useState } from 'react';
import projectApi from '../api/ProjectApi';
import { Project } from '../model/entities';
import { ProviderType } from '../model/types';
import { useIntl } from 'react-intl';
import notificationService from '../services/NotificationService';

/**
 * Custom hook for projects.
 * @returns the custom hook
 */
const useProjects = () => {
    /** HOOKS */

    const intl = useIntl();
    const [projects, setProjects] = useState<Project[]>([]);
    const projectsRef = useRef<Project[]>([]);
    projectsRef.current = projects;

    /** METHODS */

    const listProjects = useCallback(
        async (
            page: number = 0,
            size: number = 5,
            sortField: string,
            sortOrder: boolean,
            hubId: string,
            provider: ProviderType,
            searchText: string,
        ): Promise<Project[]> => {
            let searchedProjects: Project[] = [];
            try {
                const searchedProjectsPage = await projectApi.listWithPagination(
                    page,
                    size,
                    sortField,
                    sortOrder,
                    provider,
                    hubId,
                    false,
                    searchText,
                );
                searchedProjects = searchedProjectsPage.content;

                const updatedProjects = [
                    ...projectsRef.current,
                    ...searchedProjects.filter((project) => projectsRef.current.every((p) => p.id !== project.id)),
                ];
                setProjects(updatedProjects);
            } catch (error) {
                notificationService.displayError(error, intl);
            }

            return searchedProjects;
        },
        [intl],
    );

    const getProject = useCallback(
        async (
            id: string,
            provider?: ProviderType,
            hubId?: string,
            displayError: boolean = true,
        ): Promise<Project | undefined> => {
            let project = projectsRef.current.find((p) => p.id === id);
            if (!project && provider && hubId) {
                try {
                    project = await projectApi.get(id, provider, hubId);
                    if (project) {
                        setProjects((prev) => [project!, ...prev.filter((p) => p.id !== project!.id)]);
                    }
                } catch (error) {
                    if (displayError) {
                        notificationService.displayError(error, intl);
                    }
                }
            }
            return project;
        },
        [intl],
    );

    return [listProjects, getProject, projectsRef] as const;
};
export default useProjects;
